import { ChangeEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Avatar } from '@campgladiator/cgui-core.atoms.avatar'
import { Icon } from '@campgladiator/cgui-core.atoms.icon'
import { Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { GrowlMessage } from '@campgladiator/cgui-core.molecules.growl-message'
import {
  activeTrainerSelector,
  setActiveTrainer,
} from 'app/redux/features/active-trainer/active-trainer-reducer'
import { useAppDispatch } from 'app/redux/store'
import { updateTrainer } from 'services/api/trainer'
import { uploadUserImage } from 'services/api/user'
import formatPhoneNumber from 'utils/format-phone-number'
import { imageMimeType } from 'utils/regex'
import styles from './trainer-side-panel.module.scss'

const TrainerSidePanel = () => {
  const dispatch = useAppDispatch()
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const trainer = useSelector(activeTrainerSelector)
  const [isGrowlVisible, setIsGrowlVisible] = useState<boolean>(false)
  const [growlContent, setGrowlContent] = useState<string>('')
  const [image, setUserImage] = useState<string | undefined>(undefined)

  useEffect(() => {
    setUserImage(trainer?.photo)
  }, [trainer?.id, trainer?.photo])

  useEffect(() => {
    if (image?.startsWith('blob:')) {
      return () => URL.revokeObjectURL(image)
    }
  }, [image])

  const onImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const imageFile = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!imageFile || !trainer?.userId || isUploading) return

    if (!imageFile.type.match(imageMimeType)) {
      setGrowlView(
        true,
        `${imageFile.name} is not a valid image file. The image may be corrupted or an unsupported image format`,
      )
      return
    }

    setIsUploading(true)
    setIsGrowlVisible(false)
    setUserImage(URL.createObjectURL(imageFile))
    try {
      const formData = new FormData()
      formData.append('image', imageFile)

      const user = await uploadUserImage(trainer.userId, formData)
      if (!user?.imageURL?.trim()) {
        throw new Error(
          'The image upload did not return an image URL. Please try again.',
        )
      }

      const updatedTrainer = await updateTrainer(
        { ...trainer, photo: user.imageURL },
        trainer.id!,
      )
      if (!updatedTrainer?.photo?.trim()) {
        throw new Error('The trainer image was not saved. Please try again.')
      }
      dispatch(setActiveTrainer(updatedTrainer))
      setUserImage(updatedTrainer.photo)
    } catch (error) {
      setUserImage(trainer.photo)
      setGrowlView(
        true,
        error instanceof Error
          ? error.message
          : 'Unable to update trainer image',
      )
    } finally {
      setIsUploading(false)
    }
  }

  const setGrowlView = (isVisible: boolean, growlContent: string) => {
    setIsGrowlVisible(isVisible)
    setGrowlContent(growlContent)
  }

  const clearError = () => {
    setIsGrowlVisible(false)
  }

  return (
    <>
      <GrowlMessage isVisible={isGrowlVisible} onClick={clearError}>
        {growlContent}
      </GrowlMessage>

      <div className={styles.panelContainer}>
        <div className={styles.imageContainer}>
          <div className={styles.editImage}>
            <Icon.Solid name={'icon-camera'} className={styles.cameraIcon} />
            <input
              className={styles.imageInput}
              type="file"
              aria-label="Upload trainer image"
              accept="image/png, image/jpeg"
              onChange={onImageChange}
              disabled={isUploading || !trainer?.userId}
            />
          </div>
          {!image ? (
            <Avatar size="170px" className={styles.avatar} />
          ) : (
            <Avatar
              alt="Trainer's picture"
              size="170px"
              className={styles.avatar}
              src={image}
              onError={() => setUserImage('')}
            />
          )}
        </div>
        {isUploading && <span role="status">Saving image…</span>}
        <div className={styles.userName}>
          <Paragraph weight="bold" size="xlarge">
            {trainer?.firstName} {trainer?.lastName}
          </Paragraph>
        </div>

        <div className={styles.trainerButton}>
          <Paragraph weight="book" size="small" className={styles.role}>
            Trainer
          </Paragraph>
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.infoItem}>
            <Icon.Monochrome
              className={styles.infoIcon}
              name={'envelope'}
              emphasis="gladiator"
            />
            <Paragraph weight="book" size="small">
              {trainer?.email}
            </Paragraph>
          </div>
          {trainer?.contactNumber && (
            <div className={styles.infoItem}>
              <Icon.Monochrome
                className={styles.infoIcon}
                name={'phone-volume'}
                emphasis="gladiator"
              />
              <Paragraph weight="book" size="small">
                {`+1 ${formatPhoneNumber(trainer?.contactNumber)}`}
              </Paragraph>
            </div>
          )}
          <div className={styles.infoItem}>
            <Icon.Monochrome
              className={styles.infoIcon}
              name={'map-marker'}
              emphasis="gladiator"
            />
            <Paragraph weight="book" size="small">
              {trainer?.localeSummary?.regionName}
            </Paragraph>
          </div>
        </div>
      </div>
    </>
  )
}

export default TrainerSidePanel
